import { $api, type InferResponse } from '@/api/typedClient'
import { useTenantStore } from '@/stores/tenantStore'
import type { components } from '@/types/api/v1'
import { useQuery } from '@tanstack/react-query'
import { MOCK_ORDER_RESPONSE } from './seed'

// Types inferred from OpenAPI schema
export type OrderDetailResponse = InferResponse<
  '/crossroads/order/by_id',
  'post'
>

export type OrderDetailRow = components['schemas']['OrderDetailRow']
export type OrderDetailPerspectiveView =
  components['schemas']['OrderDetailPerspectiveView']
export type OrderFlag = components['schemas']['OrderFlag']
export type OrderFlagSource = components['schemas']['OrderFlagSource']

// BOL and Drop types
export type BOL = components['schemas']['BOL-Output']
export type BOLDetail = components['schemas']['BOLDetail-Output']
export type Drop = components['schemas']['Drop-Output']
export type ExecutedDrop = components['schemas']['ExecutedDrop-Output']
export type ExecutedDropDetail =
  components['schemas']['ExecutedDropDetail-Output']
export type Load = components['schemas']['Load-Output']

// Mapping key types
export type SiteMappingKey = components['schemas']['SiteMappingKey']
export type SiteTankMappingKey = components['schemas']['SiteTankMappingKey']
export type ProductMappingKey = components['schemas']['ProductMappingKey']
export type TerminalMappingKey = components['schemas']['TerminalMappingKey']
export type CounterpartyMappingKey =
  components['schemas']['CounterpartyMappingKey']

/**
 * Get display value from a MappingKey
 */
export function getMappingDisplay(
  key:
    | SiteMappingKey
    | SiteTankMappingKey
    | ProductMappingKey
    | TerminalMappingKey
    | CounterpartyMappingKey
    | null
    | undefined,
): string {
  if (!key) return '-'
  return (
    (key as { source_name?: string }).source_name ||
    (key as { source_id?: string }).source_id ||
    (key as { goid?: string }).goid ||
    '-'
  )
}

export function getMappingListDisplay(
  keys:
    | Array<TerminalMappingKey | ProductMappingKey | CounterpartyMappingKey>
    | null
    | undefined,
): string {
  if (!keys || keys.length === 0) return '-'
  return keys.map((k) => getMappingDisplay(k)).join(' OR ')
}

export function getStringListDisplay(
  arr: Array<string> | null | undefined,
): string {
  if (!arr || arr.length === 0) return '-'
  return arr.join(' OR ')
}

export function useOrderDetails(orderId: string | null | undefined) {
  const { selectedTenant } = useTenantStore()
  const isMock = false
  const shouldFetch = !!orderId && !!selectedTenant?.name && !isMock

  // Use mock data query for MOCK/DEMO order IDs
  const mockQuery = useQuery({
    queryKey: ['orderDetails', 'mock', orderId],
    queryFn: async () => MOCK_ORDER_RESPONSE,
    enabled: isMock && !!orderId,
  })

  // Use real API for other order IDs
  const apiQuery = $api.useQuery(
    'post',
    '/crossroads/order/by_id',
    {
      body: {
        order_key: {
          order_id: orderId ?? undefined,
        },
      },
    },
    {
      enabled: shouldFetch,
    },
  )

  return isMock ? mockQuery : apiQuery
}
